import { rotulosDoRegistro } from "@/features/patients/profile-dashboard/utils/resumoRegistro.js";

/**
 * Atalho "+" no cabeçalho dos cards do prontuário (salvia-781, salvia-878).
 *
 * O "+" abre o diálogo do card direto no formulário, sem passar pelo histórico
 * — o botão de três pontos ao lado continua abrindo o histórico, e os dois
 * convivem.
 *
 * Nasceu só para a consulta (salvia-781), quando os cards ficam compactos ao
 * lado do AppointmentDialog. A salvia-878 passou a mostrá-lo SEMPRE: inserir
 * dado de paciente não é necessidade exclusiva do atendimento.
 *
 * Ao gravar, o card avisa a consulta, que registra uma nota por item inserido
 * (e a nota entra na transcrição — SALVIA-881). Desde a SALVIA-881 vale para
 * qualquer inserção com conteúdo, não só a do "+". Fora do atendimento esse aviso não tem destinatário e cai no
 * vazio por desenho: a página só repassa quando o diálogo da consulta existe.
 *
 * O comportamento é idêntico nos 16 cards que aceitam inserção, por isso vive
 * aqui em vez de repetido em cada um — mesma razão do patientDataRefresh.
 *
 * O componente que usar precisa ter:
 *   - `dialogVisible` no data (o v-model do seu diálogo);
 *   - `handleDataChanged()` ou `fetchData()` para recarregar após gravar;
 *   - a prop `infoTitle`, que nomeia o card na nota.
 *
 * E no template:
 *   - o botão chamando `openQuickAdd`, exibido sob `showQuickAdd`;
 *   - `:open-direct-add="quickAddMode"` e `@data-saved="handleDataSaved"`
 *     no seu diálogo.
 */
export default {
  props: {
    /**
     * Liga o atalho. A página do dashboard passa `true` sempre (salvia-878); o
     * default segue `false` para que um card usado fora dela não ganhe o botão
     * sem alguém ter pedido.
     */
    showQuickAdd: { type: Boolean, default: false },
  },

  data() {
    return {
      /** Verdadeiro entre clicar no "+" e o diálogo fechar. */
      quickAddMode: false,
    };
  },

  watch: {
    /**
     * Fechou o diálogo: volta ao modo normal.
     *
     * O reset acontece no fechamento, e não logo após gravar, porque
     * `open-direct-add` continua em uso enquanto o diálogo está aberto —
     * desligá-lo antes faria o histórico aparecer por baixo do formulário.
     */
    dialogVisible(aberto) {
      if (!aberto) this.quickAddMode = false;
    },
  },

  methods: {
    /** Abre o diálogo do card já no formulário, pulando o histórico. */
    openQuickAdd() {
      this.quickAddMode = true;
      this.dialogVisible = true;
    },

    /**
     * O diálogo gravou. Recarrega o card e, quando a inserção veio do "+",
     * avisa a consulta com o que entrou.
     *
     * Exclusão não passa por aqui de propósito: apagar um registro não é
     * informação nova para o atendimento.
     */
    async handleDataSaved(registrado) {
      if (typeof this.handleDataChanged === "function") {
        await this.handleDataChanged();
      } else if (typeof this.fetchData === "function") {
        await this.fetchData();
      }

      // SALVIA-881: toda inserção com conteúdo vira nota da consulta, venha do
      // "+" ou do diálogo aberto pelo histórico do card. Aviso SEM conteúdo
      // (ex.: vínculo de correlação, que emite `data-saved` vazio) só pelo
      // "+" — fora dele viraria nota genérica a cada clique.
      const items = rotulosDoRegistro(registrado);
      if (this.quickAddMode || items.length) {
        this.$emit("quick-add-saved", { title: this.infoTitle, items });
      }
    },
  },
};
